import { ref, Ref, onUnmounted } from 'vue'
import api from '@/api'
import { AccountInfo, GroupInfo, CreateAccountResponse, QRCodeResponse, AccountStatusResponse } from '@/classes/WhatsApp'
import util from '@/helpers/util'

export interface AccountWithStatus {
    id: string
    name: string
    phoneNumber?: string
    status: 'disconnected' | 'connecting' | 'connected'
    groups: GroupInfo[]
}

interface AccountCreationState {
    qrCode: Ref<string | null>
    accountStatus: Ref<'disconnected' | 'connecting' | 'connected'>
}

export function useWhatsAppAccounts() {
    const accounts = ref<AccountWithStatus[]>([])
    const allGroups = ref<GroupInfo[]>([])
    const isLoadingAccounts = ref(false)
    const openAccountMenu = ref<string | null>(null)

    let currentAccountCreationState: AccountCreationState | null = null
    let qrPollInterval: number | null = null
    let statusPollInterval: number | null = null
    let qrPollAttempts = 0
    const MAX_QR_POLL_ATTEMPTS = 30

    const updateAccountInList = (accountId: string, data: { phoneNumber?: string; name?: string; status?: string }) => {
        const existingAccount = accounts.value.find(a => a.id === accountId)
        if (existingAccount) {
            if (data.phoneNumber) existingAccount.phoneNumber = data.phoneNumber
            if (data.name) existingAccount.name = data.name
            if (data.status) {
                const statusValue = data.status.toLowerCase()
                const validStatus = (statusValue === 'connected' || statusValue === 'connecting' || statusValue === 'disconnected')
                    ? statusValue as 'disconnected' | 'connecting' | 'connected'
                    : 'connecting'
                existingAccount.status = validStatus
            }
        } else {
            accounts.value.push({
                id: accountId,
                name: data.name || data.phoneNumber || accountId,
                phoneNumber: data.phoneNumber,
                status: (data.status?.toLowerCase() as 'disconnected' | 'connecting' | 'connected') || 'disconnected',
                groups: []
            })
        }
    }

    const rebuildAllGroups = () => {
        const groupsMap = new Map<string, GroupInfo>()

        for (const account of accounts.value) {
            if (account.groups && account.groups.length > 0) {
                for (const group of account.groups) {
                    if (!groupsMap.has(group.jid)) {
                        groupsMap.set(group.jid, group)
                    }
                }
            }
        }

        allGroups.value = Array.from(groupsMap.values())
    }

    const getAccountName = (accountId: string) => {
        const account = accounts.value.find((a: AccountWithStatus) => a.id === accountId)
        return account?.name || account?.phoneNumber || accountId
    }

    const getAccountPhoneNumber = (accountId: string): string | undefined => {
        const account = accounts.value.find((a: AccountWithStatus) => a.id === accountId)
        return account?.phoneNumber
    }

    const getAccountIdForGroup = (groupJid: string): string | null => {
        for (const account of accounts.value) {
            if (account.status === 'connected' && account.groups.some(g => g.jid === groupJid)) {
                return account.id
            }
        }
        return null
    }

    const checkAccountStatus = async (accountId: string) => {
        try {
            const response = await api.whatsapp.getAccountStatus(accountId)
            const data = response.data as AccountStatusResponse
            const status = data.status.toLowerCase() as 'disconnected' | 'connecting' | 'connected'
            const account = accounts.value.find(a => a.id === accountId)

            if (account) {
                account.status = status
                if (data.phoneNumber) account.phoneNumber = data.phoneNumber
                if (data.name) account.name = data.name
            } else {
                accounts.value.push({
                    id: accountId,
                    name: data.name || data.phoneNumber || accountId,
                    phoneNumber: data.phoneNumber,
                    status: status,
                    groups: []
                })
            }

            if (status !== 'connected') {
                const accountToUpdate = accounts.value.find(a => a.id === accountId)
                if (accountToUpdate) {
                    accountToUpdate.groups = []
                }
            }
        } catch (error) {
            console.error(`Failed to check status for account ${accountId}:`, error)
            const account = accounts.value.find(a => a.id === accountId)
            if (account) {
                account.status = 'disconnected'
                account.groups = []
            } else {
                accounts.value.push({
                    id: accountId,
                    name: accountId,
                    status: 'disconnected',
                    groups: []
                })
            }
        }
    }

    const loadAccounts = async () => {
        isLoadingAccounts.value = true
        try {
            const response = await api.whatsapp.getAccounts()
            const accountInfos = response.data as AccountInfo[]

            accounts.value = accountInfos.map(accountInfo => ({
                id: accountInfo.id,
                name: accountInfo.name || accountInfo.phoneNumber || accountInfo.id,
                phoneNumber: accountInfo.phoneNumber,
                status: 'disconnected' as 'disconnected' | 'connecting' | 'connected',
                groups: [] as GroupInfo[]
            }))

            for (const account of accounts.value) {
                await checkAccountStatus(account.id)
            }

            const connectedAccounts = accounts.value.filter(a => a.status === 'connected')
            if (connectedAccounts.length > 0) {
                const groupPromises = connectedAccounts.map(async (account) => {
                    try {
                        const response = await api.whatsapp.getGroups(account.id)
                        const groups = response.data as GroupInfo[]
                        account.groups = groups
                    } catch (error) {
                        console.error(`Failed to load groups for account ${account.id}:`, error)
                        account.groups = []
                    }
                })

                await Promise.all(groupPromises)
            }

            rebuildAllGroups()
        } catch (error) {
            console.error('Failed to load accounts:', error)
        } finally {
            isLoadingAccounts.value = false
        }
    }

    const stopPollingQRCode = () => {
        if (qrPollInterval) {
            clearInterval(qrPollInterval)
            qrPollInterval = null
        }
        qrPollAttempts = 0
    }

    const stopPollingStatus = () => {
        if (statusPollInterval) {
            clearInterval(statusPollInterval)
            statusPollInterval = null
        }
    }

    const startPollingQRCode = (accountId: string, state: AccountCreationState) => {
        if (qrPollInterval) {
            clearInterval(qrPollInterval)
        }

        qrPollAttempts = 0

        qrPollInterval = window.setInterval(async () => {
            if (!accountId || state.accountStatus.value === 'connected') {
                stopPollingQRCode()
                return
            }

            qrPollAttempts++

            if (qrPollAttempts > MAX_QR_POLL_ATTEMPTS) {
                console.error('QR code polling timeout after', MAX_QR_POLL_ATTEMPTS, 'attempts')
                stopPollingQRCode()
                util.showToast('QR code generation timed out. Please try creating the account again.', { icon: 'error', iconClass: 'toast-icon-circle' })
                return
            }

            try {
                const response = await api.whatsapp.getQRCode(accountId)
                const data: QRCodeResponse = response.data

                if (data.qr && data.qr.trim()) {
                    state.qrCode.value = data.qr
                    stopPollingQRCode()
                } else if (data.hasQR === false && qrPollAttempts > 5) {
                    console.warn(`QR code not available after ${qrPollAttempts} attempts. Status: ${data.status}`)
                }

                if (data.phoneNumber || data.name) {
                    updateAccountInList(accountId, {
                        phoneNumber: data.phoneNumber,
                        name: data.name
                    })
                }

                if (data.status) {
                    const statusValue = data.status.toLowerCase()
                    const validStatus = (statusValue === 'connected' || statusValue === 'connecting' || statusValue === 'disconnected')
                        ? statusValue as 'disconnected' | 'connecting' | 'connected'
                        : 'connecting'
                    state.accountStatus.value = validStatus
                    updateAccountInList(accountId, { status: data.status })
                    if (statusValue === 'connected') {
                        stopPollingQRCode()
                    }
                }
            } catch (error: any) {
                console.error(`QR code poll attempt ${qrPollAttempts} failed:`, {
                    status: error.response?.status,
                    message: error.response?.data?.error || error.message,
                    error: error
                })
            }
        }, 2000)
    }

    const startPollingStatus = (accountId: string, state: AccountCreationState) => {
        if (statusPollInterval) {
            clearInterval(statusPollInterval)
        }

        statusPollInterval = window.setInterval(async () => {
            if (!accountId || state.accountStatus.value === 'connected') {
                stopPollingStatus()
                return
            }

            try {
                const response = await api.whatsapp.getAccountStatus(accountId)
                const data = response.data as AccountStatusResponse

                if (data.phoneNumber || data.name) {
                    updateAccountInList(accountId, {
                        phoneNumber: data.phoneNumber,
                        name: data.name
                    })
                }

                if (data.status) {
                    const statusValue = data.status.toLowerCase()
                    const validStatus = (statusValue === 'connected' || statusValue === 'connecting' || statusValue === 'disconnected')
                        ? statusValue as 'disconnected' | 'connecting' | 'connected'
                        : 'connecting'
                    state.accountStatus.value = validStatus
                    updateAccountInList(accountId, { status: data.status })
                    if (statusValue === 'connected') {
                        stopPollingStatus()
                    }
                }
            } catch (error) {
                console.error('Failed to fetch account status:', error)
            }
        }, 3000)
    }

    const createAccount = async (
        accountId: string,
        qrCode: Ref<string | null>,
        accountStatus: Ref<'disconnected' | 'connecting' | 'connected'>
    ) => {
        if (!accountId.trim()) return

        currentAccountCreationState = { qrCode, accountStatus }
        qrCode.value = null
        accountStatus.value = 'connecting'

        try {
            const response = await api.whatsapp.createAccount(accountId.trim())
            const responseData = response.data as CreateAccountResponse

            if (responseData.qr) {
                qrCode.value = responseData.qr
            }

            const statusValue = responseData.status?.toLowerCase() || 'connecting'
            accountStatus.value = (statusValue === 'connected' || statusValue === 'connecting' || statusValue === 'disconnected')
                ? statusValue as 'disconnected' | 'connecting' | 'connected'
                : 'connecting'

            if (responseData.phoneNumber || responseData.name) {
                updateAccountInList(accountId, {
                    phoneNumber: responseData.phoneNumber,
                    name: responseData.name,
                    status: responseData.status
                })
            }

            startPollingQRCode(accountId, currentAccountCreationState)
            startPollingStatus(accountId, currentAccountCreationState)
        } catch (error: any) {
            console.error('Failed to create account:', error)
            const errorMessage = error.response?.data?.error || error.message || 'Failed to create account. Please try again.'
            util.showToast(`Failed to create account: ${errorMessage}`, { icon: 'error', iconClass: 'toast-icon-circle' })
            throw error
        }
    }

    const refreshAccountGroups = async (accountId: string) => {
        rebuildAllGroups()
    }

    const toggleAccountMenu = (accountId: string) => {
        if (openAccountMenu.value === accountId) {
            openAccountMenu.value = null
        } else {
            openAccountMenu.value = accountId
        }
    }

    const disconnectAccount = async (accountId: string) => {
        const account = accounts.value.find(a => a.id === accountId)
        const actionText = account?.status === 'connected' ? 'Disconnect and remove' : 'Remove'

        if (!confirm(`${actionText} account "${getAccountName(accountId)}"? This will permanently delete the account.`)) return
        try {
            if (account && account.status === 'connected') {
                try {
                    await api.whatsapp.disconnectAccount(accountId)
                } catch (disconnectError) {
                    console.warn('Failed to disconnect:', disconnectError)
                }
            }
            await api.whatsapp.deleteAccount(accountId)
            await loadAccounts()
            openAccountMenu.value = null
            util.showToast('Account permanently removed', { icon: 'success' })
        } catch (error: any) {
            console.error('Failed to remove account:', error)
            util.showToast(`Failed to remove account: ${error.response?.data?.error || error.message}`, { icon: 'error', iconClass: 'toast-icon-circle' })
        }
    }

    const handleAccountConnected = async (accountId: string) => {
        await checkAccountStatus(accountId)

        const account = accounts.value.find(a => a.id === accountId)
        if (account && account.status === 'connected') {
            try {
                const response = await api.whatsapp.getGroups(account.id)
                account.groups = response.data as GroupInfo[]
                rebuildAllGroups()
            } catch (error) {
                console.error(`Failed to load groups for newly connected account ${account.id}:`, error)
                account.groups = []
            }
        }
    }

    const updateAccountsFromData = (accountsData: Array<{ id: string; name: string; phoneNumber?: string; status: string; groups: GroupInfo[] }>) => {
        accounts.value = accountsData.map(accountData => ({
            id: accountData.id,
            name: accountData.name || accountData.phoneNumber || accountData.id,
            phoneNumber: accountData.phoneNumber,
            status: (accountData.status === 'connected' || accountData.status === 'connecting' || accountData.status === 'disconnected')
                ? accountData.status as 'disconnected' | 'connecting' | 'connected'
                : 'disconnected',
            groups: accountData.groups || []
        }))
        rebuildAllGroups()
    }

    onUnmounted(() => {
        stopPollingQRCode()
        stopPollingStatus()
    })

    return {
        accounts,
        allGroups,
        isLoadingAccounts,
        openAccountMenu,
        loadAccounts,
        createAccount,
        disconnectAccount,
        checkAccountStatus,
        refreshAccountGroups,
        toggleAccountMenu,
        handleAccountConnected,
        getAccountName,
        getAccountPhoneNumber,
        getAccountIdForGroup,
        rebuildAllGroups,
        stopPollingQRCode,
        stopPollingStatus,
        updateAccountsFromData
    }
}

